import React, { useEffect, useRef, useState } from 'react';

const ExplanationSection = ({ title, description, bgColor, textColor, image, imagePosition, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.4 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      className={`relative min-h-screen flex items-center justify-center py-20 px-6 md:px-12 ${bgColor} ${textColor}`}
    >
      <div className="container mx-auto max-w-7xl">
        {image ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {imagePosition === 'left' && (
              <div 
                className="relative h-[500px] overflow-hidden rounded-2xl shadow-2xl"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateX(0)' : 'translateX(-40px)',
                  transition: 'opacity 1s ease-out 0.2s, transform 1s ease-out 0.2s'
                }}
              >
                <img
                  src={image}
                  alt={title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            )}
            <div
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 1s ease-out 0.4s, transform 1s ease-out 0.4s'
              }}
            >
              <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8 tracking-tight">
                {title}
              </h2>
              <p className="md:text-2xl lg:text-3xl !font-light !text-4xl">
                {description}
              </p>
            </div>
            {imagePosition === 'right' && (
              <div 
                className="relative h-[500px] overflow-hidden rounded-2xl shadow-2xl"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateX(0)' : 'translateX(40px)',
                  transition: 'opacity 1s ease-out 0.2s, transform 1s ease-out 0.2s'
                }}
              >
                <img
                  src={image}
                  alt={title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
              </div>
            )}
          </div>
        ) : (
          <div className="max-w-4xl mx-auto">
            <h2 
              className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8 tracking-tight"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 1s ease-out 0.2s, transform 1s ease-out 0.2s'
              }}
            >
              {title}
            </h2>
            <p 
              className="md:text-2xl lg:text-3xl !font-light !text-4xl"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 1s ease-out 0.5s, transform 1s ease-out 0.5s'
              }}
            >
              {description}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

const ScrollExplanation = () => {
  const explanations = [
    {
      title: "Integration into existing workflows",
      description: "Our AI solutions don't exist in isolation. They become part of your team's daily operations, enhancing productivity without disrupting established processes.",
      bgColor: "bg-black",
      textColor: "text-white",
      image: "https://images.pexels.com/photos/5082570/pexels-photo-5082570.jpeg",
      imagePosition: "left"
    },
    {
      title: "From prototype to production",
      description: "We build systems that work in the real world. Every solution is tested, refined, and optimized for deployment at scale.",
      bgColor: "bg-white",
      textColor: "text-black",
      image: "https://images.pexels.com/photos/4597280/pexels-photo-4597280.jpeg",
      imagePosition: "right"
    },
    {
      title: "Transparency, reliability, and responsibility",
      description: "You understand how our AI works and why it makes specific decisions. No black boxes. No surprises. Just clear, dependable intelligence.",
      bgColor: "bg-black",
      textColor: "text-white",
      image: null,
      imagePosition: null
    }
  ];

  return (
    <>
      {explanations.map((explanation, index) => (
        <ExplanationSection
          key={index}
          title={explanation.title}
          description={explanation.description}
          bgColor={explanation.bgColor}
          textColor={explanation.textColor}
          image={explanation.image}
          imagePosition={explanation.imagePosition}
          index={index}
        />
      ))}
    </>
  );
};

export default ScrollExplanation;